import React, { useMemo } from "react";
import { Routes, Route } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { ToastContainer } from "react-toastify";
import { regionConfigs } from "./config/regions";
import Layout from "./components/Layout";
import PrivateRoute from "./common/PrivateRoute";

// Import pages - USA
import Home from "./pages/home/Home";
import AllModels from "./pages/utility-vehicles/allModels";
import OneThousandEFIModel from "./pages/utility-vehicles/oneThousandEFIModel";
import OneThousandFiveHundredModelTwo from "./pages/utility-vehicles/oneThousandFiveHundredModelTwo";
import OneThousandFiveHundredModelThree from "./pages/utility-vehicles/OneThousandFiveHundredModelThree";
import OneThousandFiveHundredModelFour from "./pages/utility-vehicles/OneThousandFiveHundredModelFour";
import TuDcOneModelFour from "./pages/utility-vehicles/TuDcOneModelFour";
import TuDcOneModelFive from "./pages/utility-vehicles/TuDcOneModelFive";
import Attachment from "./pages/utility-vehicles/attachments";
import Defend from "./pages/industries/defend";
import { AboutPage as GBAboutPage } from "./pages/about/AboutPage";
import ServiceAndSupport from "./pages/serviceAndSupport/serviceAndSupport";
import UsBlog from "./pages/usa/UsBlog/UsBlog";
import UsBlogDetail from "./pages/usa/UsBlog/UsBlogDetail";
import BecomeADealer from "./pages/becomeADealer/BecomeADealer";

// Import pages - New Zealand
import HomePage from "./pages/newZealand/home/homePage";
import AtvAccessories from "./pages/newZealand/AtvAccessories/AtvAccessories";
import NzAbout from "./pages/newZealand/NzAbout/NzAbout";
import NzPetrol from "./pages/newZealand/NzPetrol/NzPetrol";
import NzElectricAccessories from "./pages/newZealand/NzElectricAccessories/NzElectricAccessories";
import NzOrchardised from "./pages/newZealand/NzOrchardised/NzOrchardised";
import NzElectricDualMotor from "./pages/newZealand/NzElectricDualMotor/NzElectricDualMotor";
import NZGallery from "./pages/newZealand/NZGallery/NZGallery";
import NZContactUs from "./pages/newZealand/NZContactUs/NZContactUs";
import NzBlog from "./pages/newZealand/NzBlog/NzBlog";
import NzBlogDetail from "./pages/newZealand/NzBlog/NzBlogDetail";

// Import dealer portal pages
import { DealersPortal } from "./pages/dealers-portal/Dealers-portalPage";
import VehiclePartsLookup from "./pages/vehicle-parts-lookup/vehicle-parts-lookup";
import ShoppingCart from "./pages/shopping-cart/shopping-cart";
import VehiclePartsLookupDetails from "./pages/vehicle-parts-lookup-details/vehicle-parts-lookup-details";
import VehiclePartsLookupInfo from "./pages/vehicle-parts-lookup-info/vehicle-parts-lookup-info";
import InventoryList from "./pages/inventory-list/inventory-list";

const RegionApp = ({ region }) => {
    const config = regionConfigs[region];

    // Create MSAL instance for this region
    const msalInstance = useMemo(() => {
        const msalConfig = {
            auth: {
                clientId: config.msal.clientId,
                authority: config.msal.authority,
                knownAuthorities: config.msal.knownAuthorities,
                redirectUri: window.location.origin,
                postLogoutRedirectUri: window.location.origin,
                navigateToLoginRequestUrl: false,
            },
            cache: {
                cacheLocation: "sessionStorage",
                storeAuthStateInCookie: false,
            },
        };
        return new PublicClientApplication(msalConfig);
    }, [config]);

    // Initialize MSAL instance
    useMemo(() => {
        msalInstance.initialize();
    }, [msalInstance]);

    return (
        <MsalProvider instance={msalInstance}>
            <ToastContainer />
            <Layout>
                <Routes>
                    {/* Region-specific routes */}
                    {region === 'nz' ? (
                        <>
                            {/* New Zealand Routes */}
                            <Route path="/" element={<HomePage />} />
                            <Route path="/Atv-Accessories" element={<AtvAccessories />} />
                            <Route path="/about" element={<NzAbout />} />
                            <Route path="/petrol" element={<NzPetrol />} />
                            <Route path="/electric-Accessories" element={<NzElectricAccessories />} />
                            <Route path="/Orchardised" element={<NzOrchardised />} />
                            <Route path="/electric-Dual-Motor" element={<NzElectricDualMotor />} />
                            <Route path="/Gallery" element={<NZGallery />} />
                            <Route path="/ContactUs" element={<NZContactUs />} />
                            <Route path="/blog" element={<NzBlog />} />
                            <Route path="/blog/:slug" element={<NzBlogDetail />} />

                            {/* "Perfect" SEO Short URL Fallback */}
                            <Route path="/:slug" element={<NzBlogDetail />} />
                        </>
                    ) : (
                        <>
                            {/* USA Routes */}
                            <Route path="/" element={<Home />} />
                            <Route path="/utility-vehicles" element={<AllModels />} />
                            <Route path="/1000EFI" element={<OneThousandEFIModel />} />
                            <Route path="/tu-1500e-2" element={<OneThousandFiveHundredModelTwo />} />
                            <Route path="/tu-1500e-3" element={<OneThousandFiveHundredModelThree />} />
                            {/* <Route path="/tu-1500e-4" element={<OneThousandFiveHundredModelFour />} /> */}
                            <Route path="/tu-DC2500E-1" element={<TuDcOneModelFour />} />
                            <Route path="/tu-SC2500E-1" element={<TuDcOneModelFive />} />
                            <Route path="/attachments" element={<Attachment />} />
                            <Route path="/defend" element={<Defend />} />
                            <Route path="/about" element={<GBAboutPage />} />
                            <Route path="/warrnty" element={<ServiceAndSupport />} />
                            <Route path="/blog" element={<UsBlog />} />
                            <Route path="/blog/:slug" element={<UsBlogDetail />} />
                            <Route path="/become-a-dealer" element={<BecomeADealer />} />
                           
                            {/* "Perfect" SEO Short URL Fallback */}
                            <Route path="/:slug" element={<UsBlogDetail />} />
                        </>
                    )}

                    {/* Dealer portal routes (shared across regions) */}
                    <Route
                        path="/dealers-portal"
                        element={
                            <PrivateRoute>
                                <DealersPortal />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/dealers-portal/vehicle-parts-lookup"
                        element={
                            <PrivateRoute>
                                <VehiclePartsLookup />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/shopping-cart"
                        element={
                            <PrivateRoute>
                                <ShoppingCart />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/dealers-portal/vehicle-parts-lookup/:id"
                        element={
                            <PrivateRoute>
                                <VehiclePartsLookupDetails />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/dealers-portal/vehicle-parts-lookup/:id/detail"
                        element={
                            <PrivateRoute>
                                <VehiclePartsLookupInfo />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/dealers-portal/inventory-list"
                        element={
                            <PrivateRoute>
                                <InventoryList />
                            </PrivateRoute>
                        }
                    />
                </Routes>
            </Layout>
        </MsalProvider>
    );
};

export default RegionApp;
